import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ManualContent } from "@/components/ManualContent";
import { WhatsNewGuide } from "@/components/WhatsNewGuide";
import { ReleaseHistory } from "@/components/ReleaseHistory";
import { BookOpen, FileDown, ChevronLeft, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Guida() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/manuals/mine").then(({ data }) => setList(data)); }, []);

  const open = async (id) => {
    setLoading(true);
    try { const { data } = await api.get(`/manuals/${id}`); setActive(data); }
    finally { setLoading(false); }
  };

  if (active) {
    return (
      <div className="space-y-4" data-testid="guida-detail">
        <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="guida-back" className="gap-1.5 text-forest/70">
          <ChevronLeft className="h-4 w-4" /> Torna alle guide
        </Button>
        <Card className="rounded-2xl p-6">
          <h1 className="font-heading text-2xl font-semibold text-forest">{active.titolo}</h1>
          {active.descrizione && <p className="mt-1 text-sm text-muted-foreground">{active.descrizione}</p>}
          {active.has_pdf && (
            <a href={`${BACKEND_URL}/api/manuals/${active.id}/pdf`} target="_blank" rel="noopener noreferrer"
              data-testid="guida-pdf" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-forest/30 px-3 py-1.5 text-sm font-medium text-forest hover:bg-sage">
              <FileDown className="h-4 w-4" /> Scarica il PDF{active.pdf_filename ? ` (${active.pdf_filename})` : ""}
            </a>
          )}
          <div className="mt-4 border-t border-border pt-4">
            <ManualContent text={active.contenuto} />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guida-page">
      <div className="flex items-center gap-2">
        <BookOpen className="h-6 w-6 text-forest" />
        <h1 className="font-heading text-3xl font-semibold text-forest">Guida</h1>
      </div>

      <WhatsNewGuide />
      <ReleaseHistory />

      {list === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : list.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="guida-empty">
          Nessuna guida disponibile al momento. L'Agenzia attiverà i manuali quando saranno pronti per te.
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((m) => (
            <Card key={m.id} data-testid={`guida-card-${m.id}`} onClick={() => open(m.id)}
              className="cursor-pointer rounded-2xl border-border/60 p-5 transition-colors hover:border-forest/40 hover:bg-sage/30">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage text-forest"><BookOpen className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-forest">{m.titolo}</h3>
                  {m.descrizione && <p className="mt-0.5 text-sm text-muted-foreground">{m.descrizione}</p>}
                  {m.has_pdf && <span className="mt-1 inline-flex items-center gap-1 text-xs text-forest/60"><FileDown className="h-3 w-3" /> PDF allegato</span>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {loading && <div className="flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-forest" /></div>}
    </div>
  );
}
